// useAudioGuideUrl — small React hook that reads the per-property
// audio-guide URL stored as a CMS text slot (`audio_guide.<slug>`).
// Falls back to the static demo MP3 used by every card until the brand
// uploads real per-property narrations.
//
// The CMS text-slot API mirrors a single value to all three language
// fields, so we only ever read `values.en` (treated as the canonical
// URL). When the slot is empty or absent, the default demo URL is used.

import { useEffect, useState } from "react";
import {
  ensureTextLoaded, getCachedText, subscribeText,
} from "@/lib/slotCache";

export const slotForAudioGuide = (slug) => `audio_guide.${slug}`;

// Single source of truth for the default audio guide. Until the brand
// uploads per-property narrations, every play-button trigger across
// the website (homepage cards, hotel detail pages, bivouac galleries,
// admin previews) points to this same MP3 by design.
export const DEFAULT_AUDIO_GUIDE_SRC =
  "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/uxlaj8sp_Grup%20Xaluca.mp3";

const readUrl = (slot) => {
  const v = getCachedText(slot);
  if (!v) return "";
  const candidate = (v.en || v.fr || v.es || "").trim();
  return candidate;
};

export const useAudioGuideUrl = (_slug) => {
  // Currently locked to the shared brand reel for every property so all
  // audio play buttons site-wide deliver the same guide. When the
  // brand starts uploading per-property narrations, swap this back for
  // the CMS-aware `useAudioGuideUrlFromCms` below.
  return DEFAULT_AUDIO_GUIDE_SRC;
};

// Legacy CMS-aware variant kept for the admin audio page (which still
// needs to display whatever URL is configured in the CMS).
export const useAudioGuideUrlFromCms = (slug) => {
  const slot = slotForAudioGuide(slug);
  const [url, setUrl] = useState(() => readUrl(slot) || DEFAULT_AUDIO_GUIDE_SRC);

  useEffect(() => {
    let cancelled = false;
    ensureTextLoaded().then(() => {
      if (!cancelled) setUrl(readUrl(slot) || DEFAULT_AUDIO_GUIDE_SRC);
    });
    const unsub = subscribeText(slot, () => {
      setUrl(readUrl(slot) || DEFAULT_AUDIO_GUIDE_SRC);
    });
    return () => { cancelled = true; unsub(); };
  }, [slot]);

  return url;
};
